import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Avatar,
  Tooltip,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  GroupsOutlined as PeopleIcon,
  BadgeOutlined as BadgeIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { LoadRoles, LoadUsers, NewUser, DeleteUser, UpdateUserStatus, branchesList } from '../BaseURL';
import { ErrorMessage, successMessage } from '../Message';
import {
  AdminCard, AdminPageHead, ADMIN, BRAND, FONT_STACK,
  GRADIENT_GREEN, adminFieldSx, brandButtonSx, brandGhostButtonSx, EmptyState,
} from './adminUi';

// ============================================================================
//  System Users
// ============================================================================
//
//  Converted to the shared admin kit. Two things were structurally wrong
//  rather than merely unstyled:
//
//    1. Every grid used `<Grid container>` + `<Grid item xs sm md>`. MUI 7
//       ignores `item` and the bare breakpoint props outright — no error, no
//       warning. So the three summary figures, meant to be an even row of
//       three, rendered as content-width boxes bunched at the left, each a
//       different size; and inside the dialog every field took a full row,
//       which is why Role Assignment and Branch Assignment came out as two
//       mismatched blocks.
//
//    2. The screen wrapped itself in `<Container maxWidth="xl" py={4}>`. It
//       renders inside the Admin Panel, which already provides the page inset,
//       so that was a second one — the wide side margins and the big gap above
//       the title.
//
//  The dialog was also keyed to a green header on an otherwise navy product.

const Users = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [newUser, setNewUser] = useState({
    name: '',
    username: '',
    telephone: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    staff_id: '',
    branchId: ''
  });

  const [roleOptions, setRoleOptions]   = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);  // active branches only
  const [myBranch, setMyBranch] = useState(null);          // logged-in user's branch (locks the picker if not main)

  // Multi-tenancy is opt-in. Hide the Branch column + dialog card when the
  // facility runs only the main branch — keeps the UI clean for single-
  // facility deployments. Surfaces automatically as soon as a second active
  // branch exists.
  const usesBranches = branchOptions.length > 1;

  useEffect(() => {
    loadRoles();
    loadUsers();
    loadBranches();
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) setMyBranch(JSON.parse(raw)?.branch || null);
    } catch (_) {}
  }, []);

  const loadBranches = async () => {
    try {
      const { data } = await axios.get(branchesList, { params: { status: 'active' } });
      if (data?.success) setBranchOptions(data.results || []);
    } catch (_) { setBranchOptions([]); }
  };

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(LoadRoles);
      if (response.data.success) {
        setRoleOptions(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load roles',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(LoadUsers);
      if (response.data.success) {
        setUsers(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load users',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.tel.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.staff_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCreateUser = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewUser({
      name: '',
      username: '',
      telephone: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: '',
      staff_id: '',
      branchId: ''
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSaveUser = async () => {
    if (newUser.name && newUser.username && newUser.telephone &&
      newUser.password && newUser.confirmPassword && newUser.role && newUser.staff_id &&
      newUser.password === newUser.confirmPassword) {

      if (!newUser.telephone.match('^[0-9]{10}$')) {
        ErrorMessage('Error', 'Enter a valid phone number');
        return;
      }

      // Default branchId when the picker is hidden (single-facility setup)
      // or for sub-branch admins who see a locked dropdown. Falls back to
      // 1 (Main) for legacy logins whose userInfo predates multi-tenancy.
      if (!newUser.branchId) {
        newUser.branchId = myBranch?.id || 1;
      }

      try {
        const result = await Swal.fire({
          title: 'Confirm Action',
          text: "Are you sure you want to create this new user?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, create user'
        });

        if (result.isConfirmed) {
          setLoading(true);
          Swal.fire({
            title: 'Processing.......',
            text: 'Please wait',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          const response = await axios.post(NewUser, newUser);

          if (response.data.success) {
            Swal.close();
            successMessage('Success', response.data.message);
            await loadUsers();
            handleCloseDialog();
          }
        }
      } catch (error) {
        await Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to create user',
          icon: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditUser = (user) => {
    setNewUser({
      ...newUser,
      name: user.name,
      username: user.username,
      telephone: user.tel,
      email: user.email,
      role: user.role,
      staff_id: user.staff_id
    });
    handleCreateUser();
  };

  const handleDeleteUser = async (transCode) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Are you sure you want to delete user account",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        Swal.fire({
          title: 'Processing.......',
          text: 'Please wait',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const response = await axios.post(DeleteUser, { trans_code: transCode });

        if (response.data.success) {
          Swal.close();
          Swal.fire('Deleted!', 'User has been deleted.', 'success');
          await loadUsers();
        }
      } catch (error) {
        await Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete user',
          icon: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (transCode, field, currentValue) => {
    // `newStatus` was computed here and then never used — the confirm dialog
    // only ever asked "are you sure you want to change Active Status?", which
    // does not say which way it is about to go. It does now.
    const turningOn = currentValue !== 1;
    const question = field === 'is_active'
      ? (turningOn
          ? 'Activate this account? They will be able to sign in.'
          : 'Deactivate this account? They will not be able to sign in.')
      : (turningOn
          ? 'Put this user in charge?'
          : 'Remove this user from being in charge?');

    const result = await Swal.fire({
      title: 'Confirm change',
      text: question,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#123A6D',
      cancelButtonColor: '#5A6B87',
      confirmButtonText: turningOn ? 'Yes, turn it on' : 'Yes, turn it off'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      Swal.fire({
        title: 'Processing.......',
        text: 'Please wait',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const newValue = currentValue === 1 ? 0 : 1;
      
      const response = await axios.post(UpdateUserStatus, {
        trans_code: transCode,
        field: field,
        value: newValue
      });

      if (response.data.success) {
        await loadUsers();
        Swal.close();
        successMessage('Success', `Status updated successfully`);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update status',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getRoleColor = (role) => {
    const colorMap = {
      'admin': 'error',
      'manager': 'warning',
      'staff': 'info',
      'user': 'success'
    };
    return colorMap[role?.toLowerCase()] || 'primary';
  };

  return (
    // No <Container maxWidth="xl" py={4}>. This screen renders inside the
    // Admin Panel, which already provides the page inset — the Container added
    // a second one, which is what left the wide margins and the big gap above
    // "User Management".
    <Box>
      <AdminPageHead
        icon={PeopleIcon}
        title="User Management"
        subtitle="System users, their roles, and which branch they belong to"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
            disabled={loading}
            sx={{
              ...brandButtonSx,
              width: { xs: '100%', sm: 'auto' },
              py: 1, px: 2.25, fontSize: 13.5, whiteSpace: 'nowrap',
            }}
          >
            Create New User
          </Button>
        }
      />

      <Box>
        {/* Stats + search in ONE card.
            The three figures were `<Grid item xs={12} sm={6} md={3}>` — props
            MUI 7 ignores — so instead of an even row of three they rendered as
            content-width boxes bunched at the left, each a different width,
            with the search bar floating alone below them. A real grid, and the
            search moved into the same card, because "how many users are there"
            and "find one" are the same task. */}
        <AdminCard sx={{ mb: ADMIN.headGap }}>
          <Box sx={{
            display: 'grid',
            gap: { xs: 1.25, sm: 1.75 },
            gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))' },
            mb: 2,
          }}>
            {[
              { label: 'Total users',    value: users.length,         icon: PeopleIcon },
              { label: 'Active roles',   value: roleOptions.length,   icon: BadgeIcon },
              { label: 'Showing',        value: filteredUsers.length, icon: SearchIcon },
            ].map(({ label, value, icon: Icon }) => (
              <Box key={label} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0,
                border: `1px solid ${BRAND.line}`,
                borderRadius: ADMIN.radiusSm,
                px: { xs: 1.25, sm: 1.75 }, py: 1.25,
                background: BRAND.surface,
              }}>
                <Box sx={{
                  width: 34, height: 34, borderRadius: '9px', flexShrink: 0,
                  background: BRAND.navySoft, display: 'grid', placeItems: 'center',
                }}>
                  <Icon sx={{ fontSize: 18, color: BRAND.navy }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: BRAND.ink, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>
                    {value}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 11, color: BRAND.inkMuted, fontWeight: 600 }}>
                    {label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <TextField
            placeholder="Search by name, username, role, staff ID or phone…"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={adminFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: BRAND.inkFaint }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearchTerm('')}>
                    <CloseIcon sx={{ fontSize: 16, color: BRAND.inkFaint }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </AdminCard>

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: ADMIN.radius,
            boxShadow: ADMIN.shadow,
            border: `1px solid ${BRAND.line}`,
            overflowX: 'auto',
            // A seven/eight-column table does not fit a phone. It scrolls in
            // its own box rather than dragging the whole page sideways.
            '& table': { minWidth: 860 },
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  User
                </TableCell>

                <TableCell sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Contact
                </TableCell>

                <TableCell sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Role
                </TableCell>

                {usesBranches && (
                  <TableCell sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    py: 2.5
                  }}>
                    Branch
                  </TableCell>
                )}

                <TableCell sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Staff ID
                </TableCell>

                <TableCell align="center" sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Is Active
                </TableCell>

                <TableCell align="center" sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  In Charge
                </TableCell>

                <TableCell align="center" sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  py: 2.5
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      '&:last-child td': { border: 0 }
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'primary.main',
                            width: 40,
                            height: 40,
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        >
                          {getInitials(user.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.25
                          }}>
                            {user.name}
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: 'text.secondary',
                            fontSize: '0.813rem'
                          }}>
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{
                        color: 'text.primary',
                        mb: 0.25,
                        fontWeight: 500
                      }}>
                        {user.tel}
                      </Typography>
                      {user.email && (
                        <Typography variant="body2" sx={{
                          color: 'text.secondary',
                          fontSize: '0.813rem'
                        }}>
                          {user.email}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={user.role}
                        color={getRoleColor(user.role)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 1.5,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>

                    {usesBranches && (
                      <TableCell sx={{ py: 2 }}>
                        {user.branch_code ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Chip
                              label={user.branch_code}
                              size="small"
                              sx={{
                                height: 20, fontSize: 10.5, fontWeight: 700,
                                bgcolor: user.branch_id === 1 ? BRAND.greenSoft : BRAND.navySoft,
                                color:   user.branch_id === 1 ? BRAND.greenDark : BRAND.navy,
                              }}
                            />
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                              {user.branch_name}
                            </Typography>
                            {user.branch_status && user.branch_status !== 'active' && (
                              <Chip
                                label={user.branch_status}
                                size="small"
                                sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(234,179,8,0.10)', color: '#a16207' }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.78rem', fontStyle: 'italic' }}>
                            unassigned
                          </Typography>
                        )}
                      </TableCell>
                    )}

                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{
                        color: 'text.primary',
                        fontWeight: 500
                      }}>
                        {user.staff_id}
                      </Typography>
                    </TableCell>

                    {/* Both switches are disabled for the System Administrator
                        on purpose — that account cannot lock itself out. But a
                        greyed switch with no explanation just looks broken,
                        which is what these did: the only row on a fresh install
                        IS the administrator, so every toggle on screen appeared
                        dead. The tooltip says why. */}
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Tooltip
                        arrow
                        title={user.role === 'System Administrator'
                          ? 'The System Administrator cannot be deactivated'
                          : (user.is_active === 1 ? 'Active — click to deactivate' : 'Inactive — click to activate')}
                      >
                        <span>
                          <Switch
                            checked={user.is_active === 1}
                            onChange={() => handleStatusChange(user.trans_code, 'is_active', user.is_active)}
                            color="success"
                            size="small"
                            disabled={loading || user.role === 'System Administrator'}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Tooltip
                        arrow
                        title={user.role === 'System Administrator'
                          ? 'The System Administrator is always in charge'
                          : (user.is_staff === 1 ? 'In charge — click to remove' : 'Not in charge — click to grant')}
                      >
                        <span>
                          <Switch
                            checked={user.is_staff === 1}
                            onChange={() => handleStatusChange(user.trans_code, 'is_staff', user.is_staff)}
                            color="primary"
                            size="small"
                            disabled={loading || user.role === 'System Administrator'}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            onClick={() => handleEditUser(user)}
                            size="small"
                            disabled={loading}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.50'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            onClick={() => handleDeleteUser(user.trans_code)}
                            size="small"
                            disabled={loading}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.50'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={usesBranches ? 8 : 7} sx={{ borderBottom: 'none' }}>
                    <EmptyState
                      icon={PeopleIcon}
                      title="No users found"
                      body={searchTerm
                        ? `Nothing matches “${searchTerm}”.`
                        : 'No system users have been created yet.'}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          />
        </TableContainer>
      </Box>

      {/* Create User Dialog */}
      <Dialog
        open={openDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: ADMIN.radius,
            boxShadow: '0 24px 64px rgba(15,31,61,.28)',
            fontFamily: FONT_STACK,
          }
        }}
      >
        {/* Dialog Header.
            Was a flat green (#41a386) on an otherwise navy product — the one
            surface that told you this screen was built at a different time.
            Same navy lockup as every other admin dialog now. */}
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
          color: 'white',
          py: 2.25,
          px: { xs: 2, sm: 3 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
              <Box sx={{
                width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
                background: GRADIENT_GREEN, display: 'grid', placeItems: 'center',
              }}>
                <PersonIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 16.5, fontWeight: 800, lineHeight: 1.25 }}>
                  Create New User
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.75)', mt: .2 }}>
                  Their login, what they can reach, and which branch they belong to
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseDialog}
              size="small"
              sx={{
                color: 'white', flexShrink: 0,
                backgroundColor: 'rgba(255,255,255,.12)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,.24)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Sections are plain bordered blocks in a single column now.
            They were `<Paper border="2px solid primary.100">` inside
            `<Grid container spacing={4}>` — the 2px borders read as heavy black
            outlines, and because the Grid props do nothing in MUI 7 the
            sections ended up at whatever width their content forced. */}
        <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, backgroundColor: BRAND.canvas }}>
          <Box sx={{ display: 'grid', gap: { xs: 1.75, sm: 2 } }}>
            {/* Basic Information Section */}
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.75, sm: 2.25 },
                  borderRadius: ADMIN.radiusSm,
                  border: `1px solid ${BRAND.line}`,
                  backgroundColor: BRAND.surface,
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  mb: 2,
                  pb: 1.25,
                  borderBottom: `1px solid ${BRAND.line}`,
                }}>
                  <Box sx={{ width: 3, height: 16, backgroundColor: BRAND.navy, borderRadius: 1 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint }}>
                    Basic information
                  </Typography>
                </Box>

                {/* Full name spans the row; the three shorter fields share the
                    next one. Was four fields in a row of ignored Grid props,
                    which is how "Full Name" ended up the same width as
                    "Staff ID". */}
                <Box sx={{
                  display: 'grid', gap: 1.75,
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
                }}>
                  <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Full Name"
                      value={newUser.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="e.g. John Doe"
                      sx={adminFieldSx}
                    />
                  </Box>

                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Staff ID"
                      value={newUser.staff_id}
                      onChange={(e) => handleInputChange('staff_id', e.target.value)}
                      placeholder="e.g. EMP001"
                      sx={adminFieldSx}
                    />

                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Telephone Number"
                      type="number"
                      value={newUser.telephone}
                      onChange={(e) => handleInputChange('telephone', e.target.value)}
                      placeholder="e.g. 0241234567"
                      helperText="10 digits"
                      sx={adminFieldSx}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label="Email Address"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="e.g. john.doe@hospital.com"
                      helperText="Optional"
                      sx={adminFieldSx}
                    />
                </Box>
              </Paper>
            </Box>

            {/* Login Credentials Section */}
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.75, sm: 2.25 },
                  borderRadius: ADMIN.radiusSm,
                  border: `1px solid ${BRAND.line}`,
                  backgroundColor: BRAND.surface,
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  mb: 2,
                  pb: 1.25,
                  borderBottom: `1px solid ${BRAND.line}`,
                }}>
                  <Box sx={{ width: 3, height: 16, backgroundColor: BRAND.green, borderRadius: 1 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint }}>
                    Login credentials
                  </Typography>
                  <LockIcon sx={{ fontSize: 14, color: BRAND.inkFaint, ml: -.25 }} />
                </Box>

                <Box sx={{
                  display: 'grid', gap: 1.75,
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
                }}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Username"
                    value={newUser.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder="e.g. johndoe"
                    helperText="Must be unique"
                    sx={adminFieldSx}
                  />

                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={newUser.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter a strong password"
                    helperText="8 characters minimum"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword
                              ? <VisibilityOffIcon sx={{ fontSize: 18 }} />
                              : <VisibilityIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={adminFieldSx}
                  />

                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={newUser.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Re-enter the password"
                    error={!!(newUser.confirmPassword && newUser.password !== newUser.confirmPassword)}
                    helperText={
                      newUser.confirmPassword && newUser.password !== newUser.confirmPassword
                        ? "Passwords don't match"
                        : 'Must match the password'
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            size="small"
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          >
                            {showConfirmPassword
                              ? <VisibilityOffIcon sx={{ fontSize: 18 }} />
                              : <VisibilityIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={adminFieldSx}
                  />
                </Box>
              </Paper>
            </Box>

            {/* Role + Branch, side by side from sm.
                These were two full-width sections stacked, in mismatched
                colours, at whatever width their content happened to force —
                which is why they came out as two blocks of different sizes.
                They belong together: both answer "what does this account
                reach?". */}
            <Box sx={{
              display: 'grid', gap: { xs: 1.75, sm: 2 },
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: usesBranches ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' },
              alignItems: 'start',
            }}>
            {/* Role Assignment Section */}
            <Box sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.75, sm: 2.25 },
                  borderRadius: ADMIN.radiusSm,
                  border: `1px solid ${BRAND.line}`,
                  backgroundColor: BRAND.surface,
                  height: '100%',
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  mb: 2,
                  pb: 1.25,
                  borderBottom: `1px solid ${BRAND.line}`,
                }}>
                  <Box sx={{ width: 3, height: 16, backgroundColor: BRAND.navy, borderRadius: 1, mt: .25, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint }}>
                      Role assignment
                    </Typography>
                    <Typography sx={{ color: BRAND.inkMuted, fontSize: 11.5, mt: .35, lineHeight: 1.5 }}>
                      Controls what this user can see and do across the system.
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <FormControl fullWidth size="small" sx={adminFieldSx}>
                    <InputLabel shrink required>Select User Role</InputLabel>
                    <Select
                      required
                      notched
                      displayEmpty
                      value={newUser.role}
                      label="Select User Role"
                      onChange={(e) => handleInputChange('role', e.target.value)}
                    >
                      {roleOptions.map((role) => (
                        <MenuItem key={role.role} value={role.role}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
                            <Chip
                              label={role.role}
                              size="small"
                              color={getRoleColor(role.role)}
                              sx={{
                                textTransform: 'capitalize',
                                fontWeight: 600,
                                minWidth: 80
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {role.description || `User with ${role.role} privileges`}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Paper>
            </Box>

            {/* Branch Assignment Section — only shown when the facility
                runs more than one branch. Main-branch admins can assign to
                any active branch; sub-branch admins are pinned to their own. */}
            {usesBranches && (
            <Box sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.75, sm: 2.25 },
                  borderRadius: ADMIN.radiusSm,
                  border: `1px solid ${BRAND.line}`,
                  backgroundColor: BRAND.surface,
                  height: '100%',
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  mb: 2,
                  pb: 1.25,
                  borderBottom: `1px solid ${BRAND.line}`,
                }}>
                  <Box sx={{ width: 3, height: 16, backgroundColor: BRAND.navy, borderRadius: 1, mt: .25, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint }}>
                      Branch assignment
                    </Typography>
                    <Typography sx={{ color: BRAND.inkMuted, fontSize: 11.5, mt: .35, lineHeight: 1.5 }}>
                      {(myBranch && !myBranch.isMain)
                        ? 'Sub-branch admins can only register users into their own branch.'
                        : 'They will only see that branch\'s patients and operations.'}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <FormControl fullWidth size="small" sx={adminFieldSx}>
                    <InputLabel shrink required>Select Branch</InputLabel>
                    <Select
                      required
                      notched
                      value={newUser.branchId || (myBranch?.id || '')}
                      label="Select Branch"
                      onChange={(e) => handleInputChange('branchId', e.target.value)}
                      disabled={!!(myBranch && !myBranch.isMain)}
                    >
                      {(myBranch && !myBranch.isMain
                        ? branchOptions.filter(b => b.id === myBranch.id)
                        : branchOptions
                      ).map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
                            <Chip
                              label={b.code}
                              size="small"
                              sx={{
                                fontWeight: 700, minWidth: 62, textTransform: 'uppercase', fontSize: 10.5, height: 20,
                                bgcolor: b.id === 1 ? BRAND.greenSoft : BRAND.navySoft,
                                color:   b.id === 1 ? BRAND.greenDark : BRAND.navy,
                              }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {b.name}{b.id === 1 ? ' · HQ' : ''}
                              </Typography>
                              {b.location && (
                                <Typography variant="caption" color="text.secondary">
                                  {b.location}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Paper>
            </Box>
            )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: { xs: 2, sm: 2.5 }, py: 1.75,
          backgroundColor: BRAND.surface,
          borderTop: `1px solid ${BRAND.line}`,
          gap: 1.25,
          // Stacks and reverses on a phone, so the primary action sits at the
          // bottom — nearest the thumb, and last in reading order.
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& > *': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
        }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            disabled={loading}
            sx={{ ...brandGhostButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={
              loading ||
              !newUser.name ||
              !newUser.username ||
              !newUser.telephone ||
              !newUser.password ||
              !newUser.confirmPassword ||
              !newUser.role ||
              !newUser.staff_id ||
              newUser.password !== newUser.confirmPassword
            }
            sx={{ ...brandButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}
          >
            Create User Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;